import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Dropdown, Input, Modal, Table, Tabs, message } from "antd";
import { EditOutlined, MoreOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useRecoilState, useRecoilValue } from "recoil";
import { useNavigate, useSearchParams } from "react-router-dom";
import { dataKitsPageState, dataKitsState, memberState } from "../../../../state/atoms";
import DataKitApi from "../../../../api/DataKitApi";
import { DataKitDto } from "../../../../domain/DataKitDto";
import { env } from "../../../../env/env";
import { DataKitThumb } from "./DataKitThumb";
import { DataKitLocationAssignmentsTab } from "./DataKitLocationAssignmentsTab";
import { DataKitAccountMenusTab } from "./DataKitAccountMenusTab";
import { DataKitManageLocationsModal } from "./DataKitManageLocationsModal";
import { DataKitDeleteKitModal } from "./DataKitDeleteKitModal";
import { DataKitItemSearch } from "./DataKitItemSearch";
import { hasDataKitBeta } from "./dataKitAccess";
import "./DataKitListPage.scss";

const PAGE_SIZE = 100;

export function formatKitDate(value?: string): string {
  if (!value) return "––";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "––";
  const now = new Date();
  const sameDay =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();
  if (sameDay) return "Today";
  if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function DataKitListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "kits";
  const member = useRecoilValue(memberState);
  const canWrite = !!member && (member.owner || member.role === "Admin");

  const [kits, setKits] = useRecoilState(dataKitsState);
  const [page, setPage] = useRecoilState(dataKitsPageState);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [menuCount, setMenuCount] = useState<number | null>(null);

  // Row actions. One kit at a time for each, so a kit, not a boolean.
  const [editing, setEditing] = useState<DataKitDto | null>(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [managing, setManaging] = useState<DataKitDto | null>(null);
  const [deleting, setDeleting] = useState<DataKitDto | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(
    (includeArchived: boolean) => {
      setLoading(true);
      DataKitApi.listKits(includeArchived)
        .then((res) => setKits(res.data || []))
        .catch(() => message.error("Could not load item libraries"))
        .finally(() => setLoading(false));
    },
    [setKits]
  );

  // Archived kits are always fetched, so "Archived \u2022 N" can show its count while the active list
  // is on screen. Switching views is then a filter, not a refetch.
  useEffect(() => {
    load(true);
  }, [load]);

  const reload = useCallback(() => load(true), [load]);
  const archivedCount = useMemo(() => kits.filter((k) => k.archived).length, [kits]);

  // In the active view the search box searches items in every kit (DataKitItemSearch), so it no
  // longer narrows the kit list; the archived view still filters kits by name.
  const itemSearch = !showArchived && query.trim().length > 0;
  const activeKits = useMemo(() => kits.filter((k) => !k.archived), [kits]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const inView = kits.filter((k) => !!k.archived === showArchived);
    if (!q || !showArchived) return inView;
    return inView.filter(
      (k) =>
        (k.name || "").toLowerCase().includes(q) ||
        (k.description || "").toLowerCase().includes(q)
    );
  }, [kits, query, showArchived]);

  useEffect(() => {
    setPage(1);
  }, [query, showArchived, setPage]);

  const pageItems = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page]
  );

  const createKit = () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    DataKitApi.createKit({
      name,
      description: newDescription.trim(),
      archived: false,
      itemCount: 0,
      locationCount: 0,
    })
      .then((res) => {
        const created = res.data;
        setCreateOpen(false);
        setNewName("");
        setNewDescription("");
        if (created.id) navigate(`${env.dashboardPath}/account/data-kits/${created.id}`);
      })
      .catch(() => message.error("Could not create the item library"))
      .finally(() => setSaving(false));
  };

  const openEdit = (record: DataKitDto) => {
    setEditName(record.name || "");
    setEditDescription(record.description || "");
    setEditing(record);
  };

  const saveEdit = () => {
    const name = editName.trim();
    if (!editing?.id || !name) return;
    setSaving(true);
    DataKitApi.updateKit(editing.id, { ...editing, name, description: editDescription.trim() })
      .then(() => {
        setEditing(null);
        message.success("Item library saved");
        reload();
      })
      .catch(() => message.error("Could not save this item library"))
      .finally(() => setSaving(false));
  };

  // Immediate, no confirmation: archiving is the reversible path - it is what the delete
  // confirmation itself offers instead - and Restore is one click away under Archived.
  const archive = (record: DataKitDto) => {
    if (!record.id) return;
    DataKitApi.archiveKit(record.id)
      .then(() => {
        message.success("Item library archived");
        reload();
      })
      .catch(() => message.error("Could not archive this item library"));
  };

  const restore = (record: DataKitDto) => {
    if (!record.id) return;
    DataKitApi.unarchiveKit(record.id)
      .then(() => {
        message.success("Item library restored");
        reload();
      })
      .catch(() => message.error("Could not restore this item library"));
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a: DataKitDto, b: DataKitDto) =>
        (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }),
      showSorterTooltip: false,
      render: (_: string, record: DataKitDto) => (
        <div className="data-kit-name-cell">
          <DataKitThumb name={record.name || "Untitled"} />
          <div className="data-kit-name-text">
            <div className="data-kit-name">
              {record.name || "Untitled"}
              {/* Shown on hover (Figma: "Direct Edit Text"). Opens the New Data Kit fields rather
                  than renaming inline, because the description is editable too. */}
              {canWrite && !record.archived && (
                <EditOutlined
                  className="data-kit-name-edit"
                  aria-label={`Edit ${record.name || "library"}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    openEdit(record);
                  }}
                />
              )}
            </div>
            {record.description && (
              <div className="data-kit-description">{record.description}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Locations",
      dataIndex: "locationCount",
      key: "locationCount",
      width: 120,
      align: "center" as const,
      render: (count: number) => count ?? 0,
    },
    {
      title: "Items",
      dataIndex: "itemCount",
      key: "itemCount",
      width: 120,
      align: "center" as const,
      render: (count: number) => count ?? 0,
    },
    {
      title: "Last Updated",
      dataIndex: "updatedDate",
      key: "updatedDate",
      width: 160,
      render: (_: string, record: DataKitDto) => formatKitDate(record.updatedDate),
    },
    ...(canWrite
      ? [
          {
            title: "",
            key: "actions",
            width: 80,
            align: "right" as const,
            render: (_: unknown, record: DataKitDto) =>
              record.archived ? (
                <button
                  type="button"
                  className="data-kit-row-restore"
                  onClick={(e) => {
                    e.stopPropagation();
                    restore(record);
                  }}
                >
                  Restore
                </button>
              ) : (
                <Dropdown
                  trigger={["click"]}
                  placement="bottomRight"
                  overlayClassName="data-kit-row-menu"
                  menu={{
                    items: [
                      { key: "locations", label: "Manage Locations" },
                      { key: "archive", label: "Archive" },
                      { key: "delete", label: "Delete" },
                    ],
                    onClick: ({ key, domEvent }) => {
                      domEvent.stopPropagation();
                      if (key === "locations") setManaging(record);
                      if (key === "archive") archive(record);
                      if (key === "delete") setDeleting(record);
                    },
                  }}
                >
                  <Button
                    type="text"
                    icon={<MoreOutlined rotate={90} />}
                    className="data-kit-row-action"
                    aria-label={`Actions for ${record.name || "library"}`}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Dropdown>
              ),
          },
        ]
      : []),
  ];

  if (!hasDataKitBeta(member)) return null;

  return (
    <div className="data-kit-list-page">
      <header className="data-kit-list-header">
        <div className="data-kit-list-header-text">
          <h1>Item Libraries</h1>
          <p className="data-kit-subtitle">
            Account-level menu data. Assign an item library to any number of locations; a location
            can carry more than one.
          </p>
        </div>
        {canWrite && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="data-kit-new-btn"
            onClick={() => setCreateOpen(true)}
          >
            Item Library
          </Button>
        )}
      </header>

      <Tabs
        className="data-kit-list-tabs"
        // Deep-linkable so the kit's own Menus tab can send an admin here to create one - menus are
        // account-level and deliberately have no creator on the kit page.
        activeKey={activeTab}
        onChange={(key) => setSearchParams(key === "kits" ? {} : { tab: key })}
        items={[
          {
            key: "kits",
            label: "Item Libraries",
            children: (
              <>
                <div className="data-kit-toolbar">
                  <Input
                    className="data-kit-search"
                    size="large"
                    placeholder={
                      showArchived ? "Search archived item libraries..." : "Search items in every library"
                    }
                    prefix={<SearchOutlined style={{ color: "#999" }} />}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    allowClear
                  />
                  {(showArchived || archivedCount > 0) && (
                    <button
                      type="button"
                      className="data-kit-archived-link"
                      onClick={() => setShowArchived((v) => !v)}
                    >
                      {showArchived ? "\u2190 Active libraries" : `Archived \u2022 ${archivedCount}`}
                    </button>
                  )}
                </div>

                {itemSearch ? (
                  <DataKitItemSearch query={query} kits={activeKits} />
                ) : (
                  <Table
                    className="data-kit-table"
                    dataSource={pageItems}
                    columns={columns}
                    rowKey="id"
                    loading={loading}
                    rowClassName="data-kit-row"
                    pagination={
                      filtered.length > PAGE_SIZE
                        ? {
                            current: page,
                            pageSize: PAGE_SIZE,
                            total: filtered.length,
                            onChange: setPage,
                            showSizeChanger: false,
                          }
                        : false
                    }
                    locale={{
                      emptyText: query.trim()
                        ? "No item libraries match your search"
                        : showArchived
                          ? "No archived item libraries"
                          : "No item libraries yet",
                    }}
                    onRow={(record) => ({
                      onClick: () => {
                        if (!record.id) return;
                        navigate(`${env.dashboardPath}/account/data-kits/${record.id}`);
                      },
                    })}
                  />
                )}
              </>
            ),
          },
          {
            key: "menus",
            label: menuCount === null ? "Menus" : `Menus \u2022 ${menuCount}`,
            children: <DataKitAccountMenusTab canWrite={canWrite} onCountChange={setMenuCount} />,
          },
          {
            key: "locations",
            label: "Location Assignments",
            children: <DataKitLocationAssignmentsTab kits={kits} onChanged={reload} />,
          },
        ]}
      />

      <Modal
        className={"data-kit-create-modal clean one"}
        open={createOpen}
        width={600}
        onCancel={() => setCreateOpen(false)}
      >
        <header>
          <h2>New Item Library</h2>
        </header>
        <label className="data-kit-field">
          <span>Library name</span>
          <Input
            size="large"
            placeholder="e.g. Core Menus"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onPressEnter={createKit}
            maxLength={255}
            autoFocus
          />
        </label>
        <label className="data-kit-field">
          <span>Description</span>
          <Input
            size="large"
            placeholder="e.g. Food, dinner & bar"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            maxLength={255}
          />
        </label>

        <footer>
          <Button size="large" onClick={() => setCreateOpen(false)}>
            Cancel
          </Button>
          <Button
            size="large"
            className="primary"
            loading={saving}
            disabled={!newName.trim()}
            onClick={createKit}
          >
            Create
          </Button>
        </footer>
      </Modal>

      <Modal
        className={"data-kit-create-modal clean one"}
        open={!!editing}
        width={600}
        onCancel={() => setEditing(null)}
      >
        <header>
          <h2>Edit Item Library</h2>
        </header>
        <label className="data-kit-field">
          <span>Library name</span>
          <Input
            size="large"
            placeholder="e.g. Core Menus"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onPressEnter={saveEdit}
            maxLength={255}
            autoFocus
          />
        </label>
        <label className="data-kit-field">
          <span>Description</span>
          <Input
            size="large"
            placeholder="e.g. Food, dinner & bar"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            maxLength={255}
          />
        </label>
        <footer>
          <Button size="large" onClick={() => setEditing(null)}>
            Cancel
          </Button>
          <Button size="large" className="primary" loading={saving} disabled={!editName.trim()} onClick={saveEdit}>
            Save
          </Button>
        </footer>
      </Modal>

      {managing && (
        <DataKitManageLocationsModal
          kit={managing}
          onClose={() => setManaging(null)}
          onSaved={(summary) => {
            setManaging(null);
            message.success(summary);
            reload();
          }}
        />
      )}

      {deleting && (
        <DataKitDeleteKitModal
          kit={deleting}
          onClose={() => setDeleting(null)}
          onArchiveInstead={() => {
            const kit = deleting;
            setDeleting(null);
            archive(kit);
          }}
          onDeleted={() => {
            message.success(`${deleting.name || "Item library"} deleted`);
            setDeleting(null);
            reload();
          }}
        />
      )}
    </div>
  );
}
